import {
  AppPageHeader,
  ColorSwatch,
  DeleteIcon,
  EditIcon,
  Group,
  InfiniteTable,
} from '@/components/_common';
import DefaultPlusSquareDropdown from '@/components/_common/DefaultPlusSquareDropdown';
import StatusTag from '@/components/_common/StatusTag';
import {
  useAccountReceivableInstallmentCancel,
  useAccountReceivableList,
} from '@/core/domain/financial/AccountReceivable/AccountReceivable.hooks';
import {
  AccountReceivable,
  AccountReceivableColumnConfig,
  AccountReceivableListRequest,
  AccountReceivableOrderColumn,
  AccountReceivableSituation,
  AccountReceivableSituationColor,
  AccountRecivableSituationType,
} from '@/core/domain/financial/AccountReceivable/AccountReceivable.types';
import { useDebounce, useDisclosure } from '@/hooks';
import { quantityFormatter } from '@/utils/number';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { COLUMNS_WIDTH, tableSorterOrder } from '@/utils/table';
import { Space, Menu, Divider, Button, Modal, Statistic, Tooltip } from 'antd';
import { ColumnsType, SorterResult } from 'antd/es/table/interface';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import AccountReceivablePartiallyInstallmentModal from '../AccountReceivablePartiallyInstallmentModal';
import AccountReceivableSingleInstallmentModal from '../AccountReceivableSingleInstallmentModal';
import BillToReceiveExtraFilter from '../BillToReceiveExtraFilter';
import AccountReceivableAdvancedModalFilters from '../InstallmentsAdvancedModalFilters';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';
import AccountReceivableMultipleInstallmentModal from '../AccountReceivableMultipleInstallmentModal';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import formatterPrice from '@/utils/number/formatterPrice';
import usePageSearchHistory from '@/hooks/pageSearchHistory';
import { useFinancialConfigurationProfile } from '@/core/domain/financial/FinancialConfiguration/FinancialConfiguration.hooks';
import { convertColunsAccountReceivableToProfile } from '@/core/domain/financial/FinancialConfiguration/FinancialConfiguration.types';
import BankConciliationPopoverDetails from '@/components/_common/BankConciliation/BankConciliationPopoverDetails';

const AccountReceivableList: React.FC = () => {
  const intl = useIntl();

  const [installmentIds, setInstallmentIds] = useState<number[]>([]);

  const [advancedFiltersOpened, advancedFiltersAction] = useDisclosure();
  const [modalSettleMultipleOpened, modalSettleMultipleAction] =
    useDisclosure();
  const [rowsSelected, setRowsSelected] = useState<AccountReceivable[]>([]);
  const [queryParams, setQueryParams] = useState<AccountReceivableListRequest>({
    order: 'DESC',
    orderColumns: AccountReceivableOrderColumn.code,
    ...convertParams<AccountReceivableListRequest>({
      search: 'string',
      code: 'string',
      companyId: 'number',
      debtor: 'string',
      description: 'string',
      documentTypeId: 'number',
      dueDate: 'string',
      situation: 'string',
      installmentValue: 'number',
      orderColumns: 'string',
      accidentNumber: 'string',
      bankAccountId: 'number',
      costCenterId: 'number',
      documentNumber: 'string',
      licensePlate: 'string',
      workOrderNumber: 'string',
      initialDueDate: 'string',
      finalDueDate: 'string',
      initialPayDate: 'string',
      finalPayDate: 'string',
      initialEmissionDate: 'string',
      finalEmissionDate: 'string',
      order: 'string',
      observation: 'string',
      authorizationNumber: 'string',
    }),
  });

  const queryDebounce = useDebounce(queryParams, 500);
  usePersistSearchParams(queryDebounce);

  const financialConfiguration = useFinancialConfigurationProfile();
  const dataProfile = flatInfiniteData(financialConfiguration.data);

  const { setLocation } = usePageSearchHistory();
  useEffect(setLocation, [queryDebounce]);

  const { data, isFetching, fetchNextPage, hasNextPage } =
    useAccountReceivableList(queryDebounce);
  const accountReceivableItems = flatInfiniteData(data);

  const [settleInstallmentSingleItem, setSettleInstallmentSingleItem] =
    useState<AccountReceivable | null>(null);
  const [settleInstallmentPartiallyItem, setSettleInstallmentPartiallyItem] =
    useState<AccountReceivable | null>(null);

  const installmentCancel = useAccountReceivableInstallmentCancel();

  function confirmRemove(data: AccountReceivable) {
    Modal.confirm({
      title: 'Remover parcela',
      content: 'Deseja realmente remover a parcela selecionada?',
      onOk: () =>
        installmentCancel.mutateAsync({
          id: data.accountReceivableId,
          installmentId: data.id,
        }),
      okType: 'danger',
      okText: 'Remover',
    });
  }

  const columns: ColumnsType<AccountReceivable> = [
    {
      width: 140,
      dataIndex: 'code',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.code',
      }),
      sorter: true,
      key: AccountReceivableOrderColumn.code,
      render: (_, row) => (
        <Group gap={10}>
          <>{row.code}</>

          {!!row.conciliateInstallmentResponse && (
            <BankConciliationPopoverDetails
              conciliatedInstallment={row.conciliateInstallmentResponse}
            />
          )}
        </Group>
      ),
    },
    {
      dataIndex: 'installmentTag',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.installmentTag',
      }),
      width: COLUMNS_WIDTH.price,
      key: AccountReceivableOrderColumn.installmentTag,
      align: 'center',
      render: (_, row) =>
        !!row.installmentTag && (
          <Group position="center">
            <Tooltip
              title={row.installmentTag.name.toUpperCase()}
              color={row.installmentTag.color}
              placement="right"
            >
              <div>
                <ColorSwatch backgroundColor={row.installmentTag.color} />
              </div>
            </Tooltip>
          </Group>
        ),
    },
    {
      width: 220,
      dataIndex: 'companyName',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.companies',
      }),
      sorter: true,
      key: AccountReceivableOrderColumn.company,
    },
    {
      dataIndex: 'documentNumber',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.documentNumber',
      }),
      width: 220,
      key: AccountReceivableOrderColumn.documentNumber,
      sorter: true,
      ellipsis: true,
    },
    {
      width: 220,
      dataIndex: 'debtor',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.debtor',
      }),
      sorter: true,
      key: AccountReceivableOrderColumn.debtor,
    },
    {
      width: COLUMNS_WIDTH.action2,
      dataIndex: 'description',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.description',
      }),
      sorter: true,
      key: AccountReceivableOrderColumn.description,
    },
    {
      width: COLUMNS_WIDTH.price,
      dataIndex: 'documentType',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.documentType',
      }),
      sorter: true,
      key: AccountReceivableOrderColumn.documentType,
    },
    {
      width: 100,
      dataIndex: 'emissionDate',
      title: 'Dt. emissão',
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
      sorter: true,
      key: AccountReceivableOrderColumn.emissionDate,
    },
    {
      width: 100,
      dataIndex: 'dueDate',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.dueDate',
      }),
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
      sorter: true,
      key: AccountReceivableOrderColumn.dueDate,
    },
    {
      width: 100,
      dataIndex: 'payDate',
      title: 'Dt. pagamento',
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
      sorter: true,
      key: AccountReceivableOrderColumn.payDate,
    },
    {
      width: COLUMNS_WIDTH.price,
      dataIndex: 'authorizationNumber',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.authorizationNumber',
      }),
      key: AccountReceivableOrderColumn.authorizationNumber,
    },
    {
      width: COLUMNS_WIDTH.price,
      dataIndex: 'situation',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.situation',
      }),
      key: AccountReceivableOrderColumn.situation,
      render: (situation: AccountReceivableSituation) => (
        <StatusTag
          color={AccountReceivableSituationColor[situation]}
          label={AccountRecivableSituationType[situation]}
        />
      ),
    },
    {
      width: COLUMNS_WIDTH.price,
      dataIndex: 'installmentValue',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.value',
      }),
      sorter: true,
      align: 'right',
      key: AccountReceivableOrderColumn.value,
      render: (_, item) =>
        `R$ ${quantityFormatter(
          item.installmentValue + (item.discountIncrease ?? 0),
        )}`,
    },
    {
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.actions',
      }),
      width: 150,
      align: 'right',
      fixed: 'right',
      key: 'actions',
      render: (_, item) => (
        <Space>
          <DefaultPlusSquareDropdown
            disabled={
              item.situation === AccountReceivableSituation.settled ||
              !hasPermissions([Authorities.FINANCIAL_ACCOUNT_RECEIVABLE_SETTLE])
            }
          >
            <Menu>
              <Menu.Item
                key="settleSingleInstallment"
                onClick={() => setSettleInstallmentSingleItem(item)}
              >
                Liquidar
              </Menu.Item>
              <Menu.Item
                key="settlePartiallyInstallmet"
                onClick={() => setSettleInstallmentPartiallyItem(item)}
              >
                Liquidar Parcialmente
              </Menu.Item>
            </Menu>
          </DefaultPlusSquareDropdown>

          <Divider type="vertical" />

          <EditIcon
            redirect={`releaseBillToReceiver/${item.accountReceivableId}/installment/${item.id}`}
            disabled={
              !hasPermissions([Authorities.FINANCIAL_ACCOUNT_RECEIVABLE_EDIT])
            }
          />

          <Divider type="vertical" />

          <DeleteIcon
            onClick={() => confirmRemove(item)}
            disabled={
              item.hasInstallmentReceivableBill ||
              item.situation !== AccountReceivableSituation.open ||
              !hasPermissions([
                Authorities.FINANCIAL_ACCOUNT_RECEIVABLE_DELETE_INSTALLMENTS,
              ])
            }
          />
        </Space>
      ),
    },
  ];

  const activeColumns = columns.filter((item) =>
    dataProfile.some(
      (profile) =>
        (profile.active &&
          item.key &&
          convertColunsAccountReceivableToProfile[
            item.key as AccountReceivableOrderColumn
          ] === profile.column) ||
        item.key === 'actions',
    ),
  );

  function handleOrderAccountReceivable(
    sorter: SorterResult<AccountReceivable>,
  ) {
    if (sorter && sorter?.columnKey) {
      setQueryParams((prev) => {
        if (sorter && sorter?.columnKey) {
          const order = sorter.order
            ? tableSorterOrder[sorter.order]
            : tableSorterOrder.descend;

          const orderColumns = sorter.order
            ? AccountReceivableColumnConfig[sorter.columnKey]
            : undefined;

          if (prev.orderColumns === orderColumns) {
            return {
              ...prev,
              order,
            };
          }

          return {
            ...prev,
            order,
            orderColumns,
          };
        }

        return prev;
      });
    }
  }

  function setParams(params: AccountReceivableListRequest, reset?: boolean) {
    if (!reset) {
      setQueryParams((state) => ({ ...state, ...params }));
    } else {
      setQueryParams(params);
    }
  }

  const accountReceivableIds = accountReceivableItems
    .filter((accountReceivable) =>
      installmentIds.includes(accountReceivable.id),
    )
    .map((accountReceivable) => accountReceivable.accountReceivableId);

  return (
    <>
      <AppPageHeader
        onSearch={(value) => setParams({ search: value })}
        searchValue={queryParams.search}
        headerActions={({ isShowingFilters }: { isShowingFilters: boolean }) =>
          isShowingFilters && (
            <Button
              type="primary"
              onClick={() =>
                setParams(
                  {
                    order: 'DESC',
                    orderColumns: AccountReceivableOrderColumn.code,
                  },
                  true,
                )
              }
            >
              {intl.formatMessage({ id: 'commons.clear.filters' })}
            </Button>
          )
        }
        extraFilters={
          <BillToReceiveExtraFilter
            queryParams={queryParams}
            setQueryParams={setQueryParams}
          />
        }
        handleClickAdvancedFilters={advancedFiltersAction.open}
      />

      <InfiniteTable<AccountReceivable>
        columns={activeColumns}
        dataSource={accountReceivableItems}
        loading={isFetching}
        rowKey={(row) => row.id}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        onChange={(_p, _f, sorter) => {
          if (!Array.isArray(sorter)) {
            handleOrderAccountReceivable(sorter);
          }
        }}
        rowSelection={{
          preserveSelectedRowKeys: true,
          selectedRowKeys: installmentIds,
          onChange: (selectedRows: unknown, rows) => {
            setInstallmentIds(selectedRows as number[]);
            setRowsSelected(rows);
          },
          getCheckboxProps: (item) => ({
            disabled: item.situation !== AccountReceivableSituation.open,
          }),
        }}
        title={() => (
          <SpaceForButton
            style={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <Statistic
              title="Total de parcelas selecionadas"
              value={rowsSelected.reduce(
                (acc, curr) =>
                  (acc += curr?.installmentValue + curr?.discountIncrease),
                0,
              )}
              formatter={(value) => formatterPrice(Number(value))}
              valueStyle={{ color: 'rgb(160, 206, 51)' }}
            />

            <Button
              block
              type="primary"
              onClick={modalSettleMultipleAction.open}
              disabled={
                !installmentIds.length ||
                !hasPermissions([
                  Authorities.FINANCIAL_ACCOUNT_RECEIVABLE_SETTLE,
                ])
              }
            >
              {intl.formatMessage({
                id: 'releaseBillToReceiver.searchBar.settleInstallments',
              })}
            </Button>
          </SpaceForButton>
        )}
      />

      <AccountReceivableSingleInstallmentModal
        open={!!settleInstallmentSingleItem}
        onCancel={() => setSettleInstallmentSingleItem(null)}
        data={{
          installmentReceivableId: settleInstallmentSingleItem?.id,
          id: settleInstallmentSingleItem?.accountReceivableId,
          installmentValue: settleInstallmentSingleItem?.installmentValue,
          discountIncrease: settleInstallmentSingleItem?.discountIncrease,
          documentTypeId: settleInstallmentSingleItem?.documentTypeId,
          bankAccountId: settleInstallmentSingleItem?.bankAccountId,
          rateValue: settleInstallmentSingleItem?.rateValue,
        }}
      />

      <AccountReceivablePartiallyInstallmentModal
        open={!!settleInstallmentPartiallyItem}
        onCancel={() => setSettleInstallmentPartiallyItem(null)}
        data={{
          installmentReceivableId: settleInstallmentPartiallyItem?.id,
          id: settleInstallmentPartiallyItem?.accountReceivableId,
          installmentValue: settleInstallmentPartiallyItem?.installmentValue,
          discountIncrease: settleInstallmentPartiallyItem?.discountIncrease,
          documentTypeId: settleInstallmentPartiallyItem?.documentTypeId,
          bankAccountId: settleInstallmentPartiallyItem?.bankAccountId,
          rateValue: settleInstallmentPartiallyItem?.rateValue,
        }}
      />

      <AccountReceivableAdvancedModalFilters
        open={advancedFiltersOpened}
        onCancel={advancedFiltersAction.close}
        handleSetFilter={(values) =>
          setQueryParams((prev) => ({ ...prev, ...values }))
        }
        queryParams={queryParams}
      />

      <AccountReceivableMultipleInstallmentModal
        open={modalSettleMultipleOpened}
        onCancel={modalSettleMultipleAction.close}
        onFinish={() => setInstallmentIds([])}
        installmentIds={installmentIds}
        accountReceivableIds={accountReceivableIds}
        installmentMinValue={Math.min(
          ...rowsSelected.map((i) => i.installmentValue),
        )}
      />
    </>
  );
};

export default AccountReceivableList;
